import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { X, Pencil, MoreHorizontal, Archive, ArchiveRestore, Loader2, User, Flag, Clock, AlignLeft, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/tasks/ConfirmDialog";
import { TopicInspirations } from "@/components/topics/TopicInspirations";
import { TopicReview } from "@/components/topics/TopicReview";
import { TopicReadiness } from "@/components/topics/TopicReadiness";
import { STATUS_META } from "@/components/topics/topicCollection";

const PRIORITY = { low: "text-muted-foreground", normal: "text-primary", high: "text-warning" };
const fdate = (iso) => { try { return format(new Date(iso), "d MMM yyyy · h:mm a"); } catch { return "—"; } };

// View-first Topic surface (reuses the My Tasks interaction lesson): ordinary
// clicks open View, never Edit; a clear Edit Topic button hands off to the drawer.
export function TopicView({ topicId, canArchive, onClose, onEdit, onChanged }) {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);

  const load = useCallback(() => {
    apiFetch(`/topics/${topicId}`).then(setT).catch((e) => toast.error("Could not load topic", { description: e.message })).finally(() => setLoading(false));
  }, [topicId]);
  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => () => { document.body.style.pointerEvents = ""; document.body.style.overflow = ""; document.body.removeAttribute("data-scroll-locked"); }, []);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);

  const toggleArchive = async () => {
    setBusy(true);
    try {
      const next = !t.archived;
      await apiFetch(`/topics/${topicId}/archive`, { method: "PUT", body: JSON.stringify({ archived: next }) });
      toast.success(next ? "Topic archived" : "Topic restored");
      onChanged?.(); setConfirm(false); setTimeout(onClose, 0);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-drawer" data-testid="topic-view">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-border bg-card shadow-lift animate-in slide-in-from-right duration-300 sm:w-[600px] lg:w-[680px] max-sm:inset-0">
        {loading || !t ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
        ) : (
          <>
            <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/95 px-5 backdrop-blur-md sm:px-6">
              {(() => { const sm = STATUS_META[t.status] || STATUS_META.idea; const I = sm.icon; return (
                <span className={cn("inline-flex shrink-0 items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider", sm.cls)} data-testid="topic-view-status">
                  <I className="h-3.5 w-3.5" /> {t.archived ? "Archived" : sm.label}
                </span>); })()}
              <div className="flex shrink-0 items-center gap-1.5">
                <Button size="sm" onClick={onEdit} className="gap-1.5" data-testid="topic-view-edit"><Pencil className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Edit Topic</span><span className="sm:hidden">Edit</span></Button>
                {canArchive && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="topic-view-more"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => setConfirm(true)} data-testid="topic-view-archive-action">
                        {t.archived ? <><ArchiveRestore className="mr-2 h-4 w-4" /> Restore topic</> : <><Archive className="mr-2 h-4 w-4" /> Archive topic</>}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <button onClick={onClose} data-testid="topic-view-close" aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
            </header>

            {/* One canonical content column: every section shares the same inset & rhythm (spec §9). */}
            <div className="flex-1 space-y-6 overflow-y-auto overflow-x-hidden scrollbar-thin px-5 py-6 sm:px-6">
              <h1 data-testid="topic-view-title" className="min-w-0 break-words font-heading text-2xl font-semibold leading-tight text-foreground">{t.title}</h1>
              {t.summary && <p className="break-words text-[15px] leading-relaxed text-foreground/90" data-testid="topic-view-summary">{t.summary}</p>}
              {t.description && (
                <div data-testid="topic-view-description">
                  <div className="mb-1.5 flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-muted-foreground/80"><AlignLeft className="h-4 w-4 shrink-0" /> Description</div>
                  <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground/80">{t.description}</p>
                </div>
              )}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-xl border border-border/70 bg-muted/20 p-4 sm:grid-cols-2">
                <Meta icon={User} label="Owner">{t.owner_name || <span className="text-muted-foreground/50">—</span>}</Meta>
                <Meta icon={Flag} label="Priority"><span className={cn("capitalize", PRIORITY[t.priority])}>{t.priority || "normal"}</span></Meta>
                <Meta icon={Clock} label="Created">{fdate(t.created_at)}</Meta>
                <Meta icon={Clock} label="Last updated">{fdate(t.updated_at)}</Meta>
              </div>

              <div className="border-t border-border/60 pt-6">
                <TopicReview topic={t} onChanged={(fresh) => { if (fresh && fresh.id) setT(fresh); else load(); setRk((k) => k + 1); onChanged?.(); }} />
              </div>

              <div className="border-t border-border/60 pt-6">
                <TopicReadiness topicId={topicId} statusKey={t.status} version={t.content_version} refreshKey={rk} onChanged={onChanged} />
              </div>

              <div className="border-t border-border/60 pt-6">
                <TopicInspirations topicId={topicId} canEdit={!!t.can_edit && !t.archived} />
              </div>
            </div>
          </>
        )}
      </aside>
      <ConfirmDialog open={confirm} onOpenChange={(o) => !o && setConfirm(false)} busy={busy} testid="topic-archive-dialog"
        title={t?.archived ? "Restore this topic?" : "Archive this topic?"}
        description={t?.archived ? "It will return to your active topics." : "It will be removed from active topics but kept in Archived and can be restored anytime."}
        confirmLabel={t?.archived ? "Restore Topic" : "Archive Topic"} onConfirm={toggleArchive} />
    </div>,
    document.body
  );
}

function Meta({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground/80">{label}</div>
        <div className="mt-0.5 break-words text-[15px] text-foreground">{children}</div>
      </div>
    </div>
  );
}
